// Thin client for the local bridge. Relative URLs throughout, because Vite
// proxies /api to the bridge in dev and the bridge can serve the built bundle
// in production - either way there is only ever one origin.

import type { Dtc } from './obd/dtc.ts';
import type { MonitorTest, Readiness, VehicleInfo } from './obd/extended.ts';

export interface Adapter {
  id: number;
  name: string;
  transport: 'wifi' | 'serial' | 'ble' | 'sim';
  host: string | null;
  port: number | null;
  ssid: string | null;
  hasWifiKey: boolean;
  isDefault: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface Vehicle {
  /** Companies holding this car. Customer view only. */
  providers?: string[] | null;
  /** When it was first shared with this customer. */
  sharedAt?: string | null;
  vin: string;
  label: string | null;
  modelYear: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  engine: string | null;
  bodyClass: string | null;
  decodedBy: string | null;
  /** The full NHTSA payload, stored on every scan and worth more than six fields. */
  decoded: Record<string, string> | null;
  firstSeenAt: string;
  lastSeenAt: string;
  notes: string | null;
  scanCount?: number;
  lastScanAt?: string | null;
  lastDtcCount?: number | null;
}

/** One row of the platform's cross-company scan list. Named, not full: no vitals or log. */
export interface PlatformScan {
  id: number;
  companyId: number;
  company: string;
  vin: string | null;
  label: string | null;
  modelYear: number | null;
  make: string | null;
  model: string | null;
  startedAt: string;
  transport: string | null;
  milOn: boolean;
  performedBy: number | null;
  performedByName: string | null;
  dtcs: { code: string; kind: string; description: string | null }[];
}

export interface StoredScan {
  /** The provider that recorded it. Only set when a customer's history spans companies. */
  provider?: string | null;
  id: number;
  vin: string | null;
  adapterId: number | null;
  transport: string | null;
  protocol: string | null;
  startedAt: string;
  performedBy: number | null;
  performedByName: string | null;
  finishedAt: string | null;
  milOn: boolean;
  dtcCount: number;
  vitals: Record<string, number | null> | null;
  freezeFrame: { dtc: string | null; frame: number; vitals: Record<string, number | null> } | null;
  readiness: Readiness | null;
  monitorTests: (MonitorTest & { monitorLabel: string })[] | null;
  vehicleInfo: VehicleInfo | null;
  supportedPids: number[] | null;
  adapterVoltage: number | null;
  /** Kilometres, canonical. Converted at the input using the unit preference. */
  odometerKm: number | null;
  odometerSource: string | null;
  rawLog: string | null;
  dtcs: { code: string; kind: Dtc['kind']; description: string | null; system: string | null; severity: string | null }[];
  added?: string[];
  cleared?: string[];
}

export interface PartOffer {
  partNumber: string;
  brand: string | null;
  description: string | null;
  listPrice: number | null;
  yourPrice: number | null;
  currency: string;
  availability: { location: string; quantity: number }[];
  totalQuantity: number;
  source: 'worldpac' | 'mock';
}

export interface Recall {
  campaignNumber: string;
  manufacturer: string | null;
  /** ISO yyyy-mm-dd, normalised by the bridge from NHTSA's dd/mm/yyyy. */
  reportReceivedDate: string | null;
  reportReceivedRaw: string | null;
  component: string | null;
  summary: string | null;
  consequence: string | null;
  remedy: string | null;
  notes: string | null;
  modelYear: number | null;
  make: string | null;
  model: string | null;
  matchedModel: string;
}

export type RecallState = 'open' | 'scheduled' | 'completed' | 'not_applicable';

export interface RecallStatus {
  vin: string;
  campaignNumber: string;
  status: RecallState;
  /** 'dealer' records come from an authoritative feed and outrank hand entry. */
  source: 'manual' | 'dealer';
  completedOn: string | null;
  reference: string | null;
  note: string | null;
  updatedAt: string;
}

export interface RecallLookup {
  modelYear: number;
  make: string;
  requestedModel: string;
  matchedModels: string[];
  recalls: (Recall & { status: RecallStatus | null })[];
  /** Campaigns not yet marked completed or not-applicable. */
  openCount: number;
  /** True when the model could not be resolved, so an empty list proves nothing. */
  unresolved: boolean;
  notice?: string;
}

export interface CodePartsResult {
  code: string;
  system: string;
  severity: string;
  mode: 'live' | 'mock';
  notice?: string;
  candidates: {
    category: string;
    keywords: string[];
    confidence: 'high' | 'medium' | 'low';
    note?: string;
    offers: PartOffer[];
    lookupState: 'resolved' | 'needs-part-number';
    recalls?: Recall[];
  }[];
}

export interface WifiStatus {
  supported: boolean;
  /** True when the bridge is deployed, so it cannot reach a local adapter. */
  remoteBridge: boolean;
  interface: string | null;
  currentSsid: string | null;
  /** False when the OS withheld the name rather than reporting no network. */
  ssidVisible: boolean;
  address: string | null;
}

export interface ProbeResult {
  host: string;
  port: number;
  reachable: boolean;
  latencyMs: number | null;
  detail: string;
  /** Addresses on this subnet answering on the OBD port, when the configured one did not. */
  discovered: string[];
  subnet: string | null;
}

export interface DecodedVin {
  vin: string;
  valid: boolean;
  checksumOk: boolean | null;
  modelYear: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  engine: string | null;
  bodyClass: string | null;
  manufacturer: string | null;
  plantCountry: string | null;
  decodedBy: 'vpic' | 'local';
}

export type Verdict = 'critical' | 'attention' | 'watch' | 'good';

export interface SummaryFinding {
  severity: Verdict;
  title: string;
  detail: string;
  evidence: string;
}

export interface ScanSummary {
  verdict: Verdict;
  headline: string;
  findings: SummaryFinding[];
  /** 'claude' when written by the model; 'unavailable' when it could not run. */
  source: 'claude' | 'unavailable';
  notice?: string;
}

export interface Connectivity {
  online: boolean;
  detail: string;
  checkedAt: string;
  pending: { vin: string; label: string | null; missing: string[] }[];
}

export interface EnrichmentResult {
  attempted: number;
  decoded: { vin: string; summary: string }[];
  failed: { vin: string; reason: string }[];
  recallsChecked: number;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// The company a platform administrator is reading, or null for their own. Sent
// on every request rather than threaded through each call, so every screen
// shows that company without knowing it is doing so. The server ignores it
// from anyone else and keeps it read-only.
let viewingCompany: number | null = null;
export function setViewingCompany(id: number | null): void {
  viewingCompany = id;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(viewingCompany ? { 'X-View-Company': String(viewingCompany) } : {}),
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError('The bridge is not responding. Check that `npm run dev` is still running.', 0);
  }

  const text = await res.text();
  const payload = text ? safeParse(text) : null;

  if (!res.ok) {
    const message = (payload as { error?: string })?.error ?? `Request failed with ${res.status}`;
    throw new ApiError(message, res.status);
  }
  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  health: () => request<Record<string, unknown>>('/api/health'),

  authState: () =>
    request<{ unclaimed: boolean; signedIn: boolean; userId: number | null; email: string | null; company: string | null; companyId: number | null; platformAdmin: boolean; name: string | null; firstName: string | null; lastName: string | null; username: string | null; role: string | null; defaultAdapterId: number | null }>('/api/auth/state'),
  register: (b: { email: string; password: string; firstName?: string; lastName?: string; username?: string; company?: string; invite?: string }) =>
    request<{ ok: true; email: string; verified: boolean; mailSent: boolean; notice: string | null }>(
      '/api/auth/register',
      { method: 'POST', body: JSON.stringify(b) },
    ),
  verifyEmail: (b: { email: string; code: string }) =>
    request<{ ok: true }>('/api/auth/verify', { method: 'POST', body: JSON.stringify(b) }),
  resendCode: (email: string) =>
    request<{ ok: true; notice?: string | null }>('/api/auth/resend', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  login: (b: { email: string; password: string }) =>
    request<{ ok: true; email: string; role: string; verified: boolean }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(b),
    }),
  updateProfile: (b: { firstName?: string; lastName?: string; username?: string }) =>
    request<{ ok: true; name: string | null; firstName: string | null; lastName: string | null; username: string | null }>(
      '/api/auth/profile',
      { method: 'POST', body: JSON.stringify(b) },
    ),
  inviteDetails: (email: string, code: string) =>
    request<{ company: string; role: string; expiresAt: string; vehicles: string[] }>(
      `/api/auth/invite?email=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}`,
    ),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  forgotPassword: (email: string) =>
    request<{ ok: true; notice: string }>('/api/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (b: { email: string; code: string; password: string }) =>
    request<{ ok: true; notice: string }>('/api/auth/reset', { method: 'POST', body: JSON.stringify(b) }),

  // Owner-only. The server refuses these to anyone else, so a viewer reaching
  // them through the console gets a 403 rather than a hidden button.
  renameCompany: (name: string) =>
    request<{ ok: true; name: string }>('/api/admin/company', { method: 'POST', body: JSON.stringify({ name }) }),
  updatePerson: (b: { userId: number; firstName?: string; lastName?: string; username?: string; email?: string }) =>
    request<{ ok: true }>('/api/admin/people/update', { method: 'POST', body: JSON.stringify(b) }),
  setPersonDisabled: (userId: number, disabled: boolean) =>
    request<{ ok: true; disabled: boolean }>('/api/admin/people/disabled', {
      method: 'POST', body: JSON.stringify({ userId, disabled }),
    }),
  platformOverview: () =>
    request<{
      companies: {
        id: number; name: string; slug: string; created_at: string; lockedReason: string | null;
        users: number; vehicles: number; scans: number; last_activity: string | null;
      }[];
      users: {
        id: number; email: string; name: string | null; username: string | null;
        role: string; company_id: number; company: string;
        disabled_at: string | null; last_login_at: string | null;
        is_platform_admin: number; sessions: number;
      }[];
      platformAdmins: number;
    }>('/api/platform/overview'),
  platformRenameCompany: (companyId: number, name: string) =>
    request<{ ok: true }>('/api/platform/company/rename', { method: 'POST', body: JSON.stringify({ companyId, name }) }),
  platformDeleteCompany: (companyId: number, confirm: string) =>
    request<{ ok: true; deleted: string; vehicles: number; scans: number }>('/api/platform/company/delete', {
      method: 'POST', body: JSON.stringify({ companyId, confirm }),
    }),
  platformSetRole: (userId: number, role: string) =>
    request<{ ok: true }>('/api/platform/user/role', { method: 'POST', body: JSON.stringify({ userId, role }) }),
  platformSetDisabled: (userId: number, disabled: boolean) =>
    request<{ ok: true }>('/api/platform/user/disabled', { method: 'POST', body: JSON.stringify({ userId, disabled }) }),
  platformDeleteUser: (userId: number, confirm: string) =>
    request<{ ok: true; email: string; grantsRemoved: number }>('/api/platform/user/delete', {
      method: 'POST',
      body: JSON.stringify({ userId, confirm }),
    }),
  platformSetAdmin: (userId: number, on: boolean) =>
    request<{ ok: true }>('/api/platform/user/platform-admin', { method: 'POST', body: JSON.stringify({ userId, on }) }),
  platformScans: (filter: Record<string, string>) =>
    request<{
      scans: PlatformScan[];
      total: number;
      companies: { id: number; name: string; scans: number }[];
      performers: { id: number; name: string | null; company: string | null; scans: number }[];
    }>(`/api/platform/scans?${new URLSearchParams(filter)}`),
  vehicleAccess: (vin: string) =>
    request<{
      granted: { id: number; email: string; name: string | null; granted_at: string; last_login_at: string | null }[];
      pending: { email: string; created_at: string; expires_at: string | null }[];
    }>(
      `/api/admin/vehicles/${encodeURIComponent(vin)}/access`,
    ),
  resendVehicleInvite: (vin: string, email: string) =>
    request<{ ok: true; email: string; mailSent: boolean; notice: string | null; code: string | null }>(
      '/api/admin/vehicles/access/resend',
      { method: 'POST', body: JSON.stringify({ vin, email }) },
    ),
  grantVehicle: (vin: string, email: string, revoke = false) =>
    request<{
      ok: true; vin: string; email: string; granted: boolean;
      invited?: boolean; mailSent?: boolean; notice?: string | null; code?: string | null;
    }>('/api/admin/vehicles/access', {
      method: 'POST',
      body: JSON.stringify({ vin, email, revoke }),
    }),
  customers: () =>
    request<{
      customers: {
        id: number; email: string; name: string | null; last_login_at: string | null;
        disabled_at: string | null; vehicles: number; first_granted: string;
      }[];
    }>('/api/admin/customers'),
  revokeCustomer: (userId: number) =>
    request<{ ok: true; email: string; removed: number; deleted: boolean; heldElsewhere: number }>(
      '/api/admin/customers/revoke', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  people: () =>
    request<{
      company: { id: number; name: string };
      users: {
        id: number; email: string; name: string | null; username: string | null;
        first_name: string | null; last_name: string | null; disabled_at: string | null;
        last_login_at: string | null; role: string;
        email_verified_at: string | null; created_at: string; last_seen_at: string | null; sessions: number;
      }[];
      invites: {
        id: number; email: string; role: string; created_at: string;
        expires_at: string; invited_by_email: string | null;
      }[];
      you: number | null;
    }>('/api/admin/people'),
  invite: (b: { email: string; role: string }) =>
    request<{ ok: true; email: string; role: string; mailSent: boolean; notice: string | null; code: string | null }>(
      '/api/admin/invites',
      { method: 'POST', body: JSON.stringify(b) },
    ),
  revokeInvite: (id: number) =>
    request<{ ok: true }>('/api/admin/invites/revoke', { method: 'POST', body: JSON.stringify({ id }) }),
  setRole: (userId: number, role: string) =>
    request<{ ok: true }>('/api/admin/people/role', { method: 'POST', body: JSON.stringify({ userId, role }) }),
  removePerson: (userId: number) =>
    request<{ ok: true }>('/api/admin/people/remove', { method: 'POST', body: JSON.stringify({ userId }) }),

  connectivity: (force = false) => request<Connectivity>(`/api/connectivity${force ? '?force=1' : ''}`),
  runEnrichment: () => request<EnrichmentResult>('/api/enrichment/run', { method: 'POST' }),

  listAdapters: () => request<Adapter[]>('/api/adapters'),
  // `transport` is widened to string here because the editor form holds a raw
  // select value; the bridge validates it against the allowed set.
  saveAdapter: (a: Omit<Partial<Adapter>, 'transport'> & { name: string; transport: string; wifiKey?: string }) =>
    request<Adapter>('/api/adapters', { method: 'POST', body: JSON.stringify(a) }),
  deleteAdapter: (id: number) => request<{ ok: true }>(`/api/adapters/${id}`, { method: 'DELETE' }),
  /** Your own adapter. Personal, so it follows you rather than the company. */
  setMyAdapter: (adapterId: number | null) =>
    request<{ ok: true; defaultAdapterId: number | null }>('/api/auth/adapter', {
      method: 'POST',
      body: JSON.stringify({ adapterId }),
    }),

  wifiStatus: () => request<WifiStatus>('/api/wifi/status'),
  wifiJoin: (adapterId: number) =>
    request<{ ok: boolean; ssid: string; message: string }>('/api/wifi/join', {
      method: 'POST',
      body: JSON.stringify({ adapterId }),
    }),
  wifiProbe: (adapterId?: number) =>
    request<ProbeResult>('/api/wifi/probe', { method: 'POST', body: JSON.stringify({ adapterId }) }),

  decodeVin: (vin: string) => request<DecodedVin>(`/api/vin/${encodeURIComponent(vin)}`),

  listVehicles: () => request<Vehicle[]>('/api/vehicles'),
  getVehicle: (vin: string) => request<Vehicle>(`/api/vehicles/${encodeURIComponent(vin)}`),
  labelVehicle: (vin: string, label: string, notes?: string) =>
    request<Vehicle>(`/api/vehicles/${encodeURIComponent(vin)}`, {
      method: 'PATCH',
      body: JSON.stringify({ label, notes }),
    }),
  updateVehicle: (
    vin: string,
    fields: { model?: string; trim?: string; engine?: string; bodyClass?: string; modelYear?: number | null },
  ) =>
    request<Vehicle>(`/api/vehicles/${encodeURIComponent(vin)}`, { method: 'PATCH', body: JSON.stringify(fields) }),
  redecodeVin: (vin: string) =>
    request<Vehicle>(`/api/vehicles/${encodeURIComponent(vin)}/redecode`, { method: 'POST' }),
  recalls: (vin: string) => request<RecallLookup>(`/api/vehicles/${encodeURIComponent(vin)}/recalls`),
  setRecallStatus: (
    vin: string,
    campaign: string,
    body: {
      status: RecallState;
      completedOn?: string | null;
      reference?: string | null;
      note?: string | null;
      force?: boolean;
    },
  ) =>
    request<RecallStatus>(`/api/vehicles/${encodeURIComponent(vin)}/recalls/${encodeURIComponent(campaign)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  clearRecallStatus: (vin: string, campaign: string) =>
    request<{ ok: true }>(`/api/vehicles/${encodeURIComponent(vin)}/recalls/${encodeURIComponent(campaign)}`, {
      method: 'DELETE',
    }),
  vehicleScans: (vin: string) => request<StoredScan[]>(`/api/vehicles/${encodeURIComponent(vin)}/scans`),

  // Units are written into the prose, so the preference has to travel with the
  // request rather than being applied to the result.
  scanSummary: (id: number, refresh = false, units: 'metric' | 'imperial' = 'metric') =>
    request<ScanSummary>(`/api/scans/${id}/summary?units=${units}${refresh ? '&refresh=1' : ''}`),
  recentScans: () => request<StoredScan[]>('/api/scans'),
  mappedCodes: (codes: string[]) =>
    request<{ mapped: string[] }>(`/api/parts/mapped?codes=${encodeURIComponent(codes.join(','))}`),
  fleet: () =>
    request<{
      vin: string; label: string | null; modelYear: number | null; make: string | null; model: string | null;
      /** Companies holding this car. Only set for a customer, who may have several. */
      providers: string[] | null;
      scanCount: number; lastScanAt: string | null; dtcCount: number; milOn: boolean;
      odometerKm: number | null; odometerAt: string | null;
      partsAvailable: boolean; settledRecalls: number;
    }[]>('/api/fleet'),
  maintenance: (vin: string) =>
    request<{
      vin: string; make: string | null; model: string | null;
      odometerKm: number | null; odometerAt: string | null; readingCount: number; firstSeenAt: string;
      items: {
        id: number; make: string; model: string | null; item: string; category: string;
        intervalKm: number | null; intervalMonths: number | null; intervalHours: number | null;
        trigger: string | null; note: string | null;
        doneAt: string | null; doneKm: number | null;
      }[];
    }>(`/api/vehicles/${encodeURIComponent(vin)}/maintenance`),
  markMaintenanceDone: (vin: string, itemId: number, doneAt: string | null, doneKm: number | null) =>
    request<{ ok: true }>(`/api/vehicles/${encodeURIComponent(vin)}/maintenance/${itemId}/done`, {
      method: 'POST',
      body: JSON.stringify({ doneAt, doneKm }),
    }),
  setScanOdometer: (id: number, odometerKm: number | null) =>
    request<{ odometerKm: number | null }>(`/api/scans/${id}/odometer`, {
      method: 'PATCH',
      body: JSON.stringify({ odometerKm }),
    }),
  saveScan: (scan: {
    /** Read from the vehicle, in kilometres. Absent when it reports none. */
    odometerKm?: number | null;
    odometerSource?: string | null;
    vin: string | null;
    adapterId: number | null;
    transport: string | null;
    protocol: string | null;
    milOn: boolean;
    vitals: unknown;
    freezeFrame: unknown;
    readiness: unknown;
    monitorTests: unknown;
    vehicleInfo: unknown;
    supportedPids: unknown;
    adapterVoltage: number | null;
    rawLog: string | null;
    dtcs: { code: string; kind: string; description: string; system: string; severity: string }[];
  }) => request<StoredScan>('/api/scans', { method: 'POST', body: JSON.stringify(scan) }),
  deleteScan: (id: number) => request<{ ok: true }>(`/api/scans/${id}`, { method: 'DELETE' }),

  partsForCode: (
    code: string,
    vehicle?: { vin?: string; make?: string | null; model?: string | null; modelYear?: number | null },
  ) => {
    const q = new URLSearchParams();
    if (vehicle?.vin) q.set('vin', vehicle.vin);
    if (vehicle?.make) q.set('make', vehicle.make);
    if (vehicle?.model) q.set('model', vehicle.model);
    if (vehicle?.modelYear) q.set('year', String(vehicle.modelYear));
    const suffix = q.toString() ? `?${q}` : '';
    return request<CodePartsResult>(`/api/parts/code/${encodeURIComponent(code)}${suffix}`);
  },
};
